import React, { useMemo } from 'react';
import { MenuGroup, Section } from '@atlaskit/menu';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useFetchedToolSections } from '@atlassian/jira-software-connect-toolbar/src/controllers/index.tsx';
import { BoardConnectMenuItems } from '@atlassian/jira-software-connect-toolbar/src/ui/board-connect-menu-items/index.tsx';
import type { BoardMenuItemLocation } from '@atlassian/jira-software-connect-toolbar/src/ui/board-connect-menu-items/types.tsx';
import { findSection } from '@atlassian/jira-software-connect-toolbar/src/ui/board-connect-menu-items/utils.tsx';
import { useBacklogSelector } from '../../../../state/index.tsx';
import { getProjectKey, getRapidViewId } from '../../../../state/selectors/app/index.tsx';
import { operationsSelector } from '../../../../state/selectors/extension/index.tsx';
import Operations from '../../extension/index.tsx';
import ConfigureBoardItem from '../configure-board-item/index.tsx';
import { CreateBoardMenuItem } from '../create-board/index.tsx';
import { FullscreenItem } from '../fullscreen-item/index.tsx';
import ManageCustomFiltersItem from '../manage-custom-filters-item/index.tsx';
import type { Props, MenuItemProps } from '../types.tsx';
import { BoardConnectItems } from './connect-items/index.tsx';

export type MenuContentProps = Props & MenuItemProps;

const BacklogMenuContentOld = ({
	isCompanyManaged,
	canEditBoardConfig,
	onClosePopup,
}: MenuContentProps) => {
	const boardId = useBacklogSelector(getRapidViewId);
	const projectKey = useBacklogSelector(getProjectKey);
	const onClickWebItem = fg('make_board_menu_popup') ? onClosePopup : undefined;

	return (
		<MenuGroup>
			<Section>
				{isCompanyManaged ? (
					<>
						<ConfigureBoardItem />

						<BoardConnectMenuItems
							boardId={boardId}
							projectKey={projectKey}
							location="board-tools-1"
							mode="plan"
							onClickWebItem={onClickWebItem}
						/>

						{canEditBoardConfig && <CreateBoardMenuItem onClosePopup={onClosePopup} />}

						<BoardConnectMenuItems
							boardId={boardId}
							projectKey={projectKey}
							location="view-actions-print"
							mode="plan"
							onClickWebItem={onClickWebItem}
						/>
					</>
				) : (
					<ManageCustomFiltersItem isDisabled={!canEditBoardConfig} />
				)}
			</Section>
		</MenuGroup>
	);
};

const BacklogMenuContentNew = ({
	isCompanyManaged,
	canEditBoardConfig,
	onClosePopup,
}: MenuContentProps) => {
	const boardId = useBacklogSelector(getRapidViewId);
	const projectKey = useBacklogSelector(getProjectKey);
	const onClickWebItem = fg('make_board_menu_popup') ? onClosePopup : undefined;

	const hasOperations = useBacklogSelector(operationsSelector).length > 0;

	const toolSections = useFetchedToolSections({ boardId, projectKey, mode: 'plan' });
	const shouldRenderSection = useMemo(() => {
		const connectMenuItemLocations: BoardMenuItemLocation[] = [
			'board-tools-1',
			'view-actions-print',
		];
		return Boolean(
			toolSections &&
				(connectMenuItemLocations
					.map(
						(location) =>
							(findSection(toolSections, location)?.subItems?.filter((item) => item.id != null)
								.length ?? 0) > 0,
					)
					.includes(true) ||
					toolSections?.filter((section) => section.id !== 'board-tools-section').length > 0),
		);
	}, [toolSections]);

	return (
		<MenuGroup maxWidth={240}>
			<Section>
				{fg('jira_nav4_beta_drop_1') && (
					<FullscreenItem onClosePopup={fg('jira_nav4_beta_drop_2') ? onClosePopup : undefined} />
				)}
				{isCompanyManaged ? (
					<>
						<ConfigureBoardItem />
						{canEditBoardConfig && <CreateBoardMenuItem onClosePopup={onClosePopup} />}
						{fg('jira_nav4_eap_drop_2') ? (
							<BoardConnectItems projectKey={projectKey} boardId={boardId} />
						) : (
							shouldRenderSection && (
								<Section hasSeparator>
									<BoardConnectMenuItems
										boardId={boardId}
										projectKey={projectKey}
										location="board-tools-1"
										mode="plan"
										onClickWebItem={onClickWebItem}
									/>
									<BoardConnectMenuItems
										boardId={boardId}
										projectKey={projectKey}
										location="view-actions-print"
										mode="plan"
										onClickWebItem={onClickWebItem}
									/>
									<BoardConnectMenuItems
										boardId={boardId}
										projectKey={projectKey}
										mode="plan"
										onClickWebItem={onClickWebItem}
									/>
								</Section>
							)
						)}
					</>
				) : (
					<>
						<ManageCustomFiltersItem isDisabled={!canEditBoardConfig} />
						{hasOperations && (
							<Section hasSeparator>
								<Operations />
							</Section>
						)}
					</>
				)}
			</Section>
		</MenuGroup>
	);
};

const BacklogMenuContent = componentWithCondition(
	() => getWillShowNav4() && fg('move-connect-addons-board-backlog-nav4'),
	BacklogMenuContentNew,
	BacklogMenuContentOld,
);

export default BacklogMenuContent;
